import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import Container from '../ui/Container';
import Button from '../ui/Button';
import Wordmark from '../ui/Wordmark';
import MobileNav from './MobileNav';
import { primaryNavigation, primaryCta } from '../../utils/navigation';
import { ROUTES } from '../../utils/routes';
import businessInfo from '../../utils/businessInfo';

/**
 * Sticky site header.
 *
 * Holds the two things every page needs: the way around the site, and the one
 * action the site exists to produce. The desktop navigation and the mobile
 * drawer are fed from the same list in utils/navigation.js, so they cannot
 * drift apart.
 *
 * The header is transparent over the hero and gains a background and border
 * once the page has scrolled, so the hero reads as a full-bleed opening rather
 * than a panel under a bar.
 */
const Nav = () => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const { pathname, hash } = useLocation();
  const toggleRef = useRef(null);

  /**
   * Closes the drawer and puts focus back on the control that opened it, so a
   * keyboard user is returned to where they were rather than to the top of the
   * document.
   */
  const closeDrawer = useCallback(({ returnFocus = true } = {}) => {
    setIsDrawerOpen(false);
    if (returnFocus) {
      requestAnimationFrame(() => toggleRef.current?.focus());
    }
  }, []);

  // A drawer left open across a navigation would cover the page it just opened.
  useEffect(() => {
    closeDrawer({ returnFocus: false });
  }, [pathname, hash, closeDrawer]);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  /**
   * The assistant page carries no marketing action of its own, so the header
   * withholds the call to action there. A page whose one job is answering the
   * visitor's question does not want a second ask in the bar above it; the
   * assistant's own fallback line reaches the same form, and the Services page
   * is one click away.
   */
  const showCta = pathname !== ROUTES.home;

  return (
    <header className={`site-header${isScrolled ? ' site-header--scrolled' : ''}`}>
      <Container className="site-header__inner">
        <Link
          to={ROUTES.home}
          className="site-header__brand"
          aria-label={`${businessInfo.name} — home`}
        >
          <Wordmark />
        </Link>

        <nav className="site-nav" aria-label="Primary">
          <ul className="site-nav__list">
            {primaryNavigation.map((item) => (
              <li key={item.to} className="site-nav__item">
                <NavLink
                  to={item.to}
                  end={item.to === ROUTES.home}
                  className={({ isActive }) =>
                    `site-nav__link${isActive ? ' is-active' : ''}`
                  }
                >
                  {item.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>

        <div className="site-header__actions">
          {showCta ? (
            <Button
              to={primaryCta.to}
              variant="primary"
              size="sm"
              className="site-header__cta"
            >
              {primaryCta.label}
            </Button>
          ) : null}

          <button
            type="button"
            ref={toggleRef}
            className="nav-toggle"
            aria-expanded={isDrawerOpen}
            aria-controls="mobile-navigation"
            onClick={() => setIsDrawerOpen((open) => !open)}
          >
            <span className="visually-hidden">
              {isDrawerOpen ? 'Close menu' : 'Open menu'}
            </span>
            <span className="nav-toggle__bars" aria-hidden="true">
              <span />
              <span />
              <span />
            </span>
          </button>
        </div>
      </Container>

      <MobileNav isOpen={isDrawerOpen} onClose={closeDrawer} />
    </header>
  );
};

export default Nav;
